// src/components/aiinsight/EditableHeaderBlock.jsx
// Branding header block for AI Insight report canvas.
// Controlled via the ReportBrandingPanel floating editor.
// No longer supports inline editing; strictly reactive to config state.

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTenantExportBranding } from '../../hooks/useTenantExportBranding';

// Expanded default header config
export const DEFAULT_HEADER_CONFIG = {
    align: 'center',
    layout: 'stack',
    showLogo: true,
    subtitle: '',
    showSubtitle: false,
    companyName: '',
    showCompanyName: false,
    paddingTop: 0,
    paddingBottom: 24,
    paddingHorizontal: 0,
    showDivider: true,
    dividerStyle: 'solid',
    dividerColor: '#e2e8f0',
};

const EditableHeaderBlock = ({
    config = DEFAULT_HEADER_CONFIG,
    isExporting = false,
    sx = {},
}) => {
    const { logoUrl, portalName, loading } = useTenantExportBranding();
    const [logoBase64, setLogoBase64] = useState(null);

    useEffect(() => {
        if (logoUrl) {
            if (logoUrl.startsWith('data:')) {
                setLogoBase64(logoUrl);
                return;
            }
            fetch(logoUrl)
                .then(r => r.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onloadend = () => setLogoBase64(reader.result);
                    reader.readAsDataURL(blob);
                })
                .catch(() => setLogoBase64(logoUrl));
        } else {
            setLogoBase64(null);
        }
    }, [logoUrl]);

    if (loading) return null;

    const hasLogo = config.showLogo && (logoUrl || portalName);
    const hasSubtitle = config.showSubtitle && config.subtitle;
    const hasCompany = config.showCompanyName && (config.companyName || portalName);
    const hasContent = hasLogo || hasSubtitle || hasCompany;

    if (!hasContent) return null;

    const align = config.align || 'center';
    const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
    const textAlignMap = { left: 'left', center: 'center', right: 'right' };
    const justifyContent = alignMap[align] || 'center';
    const textAlign = textAlignMap[align] || 'center';
    const isInline = config.layout === 'inline';

    return (
        <Box
            id="export-branding-header"
            sx={{
                width: '100%',
                pt: config.paddingTop ?? 0,
                pb: config.paddingBottom ?? 24,
                px: config.paddingHorizontal ?? 0,
                mb: 4,
                borderBottom: config.showDivider 
                    ? `1px ${config.dividerStyle || 'solid'} ${config.dividerColor || '#e2e8f0'}`
                    : 'none',
                position: 'relative',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isInline ? (align === 'right' ? 'row-reverse' : 'row') : 'column',
                    alignItems: isInline ? 'center' : justifyContent,
                    justifyContent: isInline ? justifyContent : 'center',
                    gap: isInline ? 2 : 0,
                    textAlign,
                }}
            >
                {/* Logo Section */}
                {config.showLogo && (
                    <Box sx={{ flexShrink: 0, mb: !isInline && (hasSubtitle || hasCompany) ? 1.5 : 0 }}>
                        {logoUrl ? (
                            <img src={logoBase64 || logoUrl} alt="" style={{ maxHeight: 74, maxWidth: 320, objectFit: 'contain', display: 'block' }} />
                        ) : portalName ? (
                            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.main' }}>{portalName}</Typography>
                        ) : null}
                    </Box>
                )}

                {/* Text Section */}
                {(hasCompany || hasSubtitle) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isInline ? (align === 'right' ? 'flex-end' : 'flex-start') : justifyContent }}>
                        {config.showCompanyName && (
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                {config.companyName || portalName || ''}
                            </Typography>
                        )}
                        {config.showSubtitle && config.subtitle && (
                            <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
                                {config.subtitle}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default EditableHeaderBlock;
